import { useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Banner, Button, Heading, Spinner } from "@panelgenie/shared-ui";
import { reportsKeys } from "../../trpc/query-keys.js";

/**
 * Stripe Checkout return landing.
 *
 * Stripe redirects here on a successful Checkout completion. The
 * route reloads the Clerk user (so the webhook-applied tier on
 * `publicMetadata` lands in `useAuth()` snapshot), invalidates
 * tier-derived caches, then surfaces a confirmation.
 *
 * The webhook may not have landed yet (Stripe webhook delivery is
 * best-effort, typically sub-second). The Clerk reload + cache
 * invalidation cover both "webhook already applied" and "webhook
 * still in flight" paths.
 */
export const Route = createFileRoute("/billing/return")({
  component: BillingReturnPage,
});

type ReloadState = "pending" | "completed" | "failed";

function BillingReturnPage() {
  const { user, isLoaded } = useUser();
  const queryClient = useQueryClient();
  const [reloadState, setReloadState] = useState<ReloadState>("pending");

  useEffect(() => {
    if (!isLoaded || user === undefined || user === null) return;
    let cancelled = false;
    void (async () => {
      try {
        await user.reload();
        if (cancelled) return;
        await queryClient.invalidateQueries({
          queryKey: reportsKeys.listAvailable(),
        });
        if (!cancelled) setReloadState("completed");
      } catch {
        if (!cancelled) setReloadState("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, user, queryClient]);

  if (!isLoaded) {
    return (
      <BillingPanel>
        <PendingRow>Loading your session…</PendingRow>
      </BillingPanel>
    );
  }
  if (user === null || user === undefined) {
    return (
      <BillingPanel>
        <Banner variant="info" title="Sign in to confirm">
          Please sign in to confirm your upgrade. If you completed payment,
          your upgrade is already applied to your account.
        </Banner>
      </BillingPanel>
    );
  }
  if (reloadState === "pending") {
    return (
      <BillingPanel>
        <PendingRow>Confirming your upgrade…</PendingRow>
      </BillingPanel>
    );
  }
  if (reloadState === "failed") {
    return (
      <BillingPanel>
        <Banner variant="warning" title="Sync delayed">
          We couldn&apos;t refresh your account state. Refresh the page or
          sign out and back in. Your payment is recorded — gated features
          unlock once we sync.
        </Banner>
      </BillingPanel>
    );
  }
  return (
    <BillingPanel>
      <Banner variant="success" title="Upgrade complete">
        Gated features are now unlocked across Panel Genie.
      </Banner>
      <Link to="/" className="block mt-4 no-underline">
        <Button size="lg" className="w-full sm:w-auto">
          Back to your sites
        </Button>
      </Link>
    </BillingPanel>
  );
}

type BillingPanelProps = {
  readonly children: ReactNode;
};

const BillingPanel = ({ children }: BillingPanelProps) => {
  return (
    <main
      aria-label="billing-return"
      className="mx-auto max-w-xl px-4 py-10 text-center"
    >
      <Heading level="h1" className="mb-6">
        Panel Genie
      </Heading>
      <div className="text-left">{children}</div>
    </main>
  );
};

const PendingRow = ({ children }: { readonly children: ReactNode }) => (
  <div className="flex items-center gap-3 text-base text-fg">
    <Spinner size="md" />
    <span>{children}</span>
  </div>
);
